import { SectionContentSkeleton, SectionSkeleton, Stack } from '@cmg/design-system';
import defaultTo from 'lodash/defaultTo';
import React from 'react';

import ServerErrorsBanner from '../../../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { useFeatureToggles } from '../../../../../../../../common/config';
import usePollingWhenVisible from '../../../../../../../../common/hooks/usePollingWhenVisible';
import useCheckPassiveOrderBookAccess from '../../../../../../../../common/util/check-access/useCheckInstitutionalPassiveOrderBookAccess';
import {
  IndicationStatus as IndicationStatusType,
  InstitutionalIndicationOrderType,
} from '../../../../../../../../graphql';
import { useIndicationActivitySyndicateContext } from '../../../../context/IndicationActivitySyndicateContext';
import { useInstitutionalIndicationAcknowledgementsQuery } from '../../../indication-form/components/indication-details/components/acknowledgement/hooks/useInstitutionalIndicationAcknowledgementsQuery';
import IndicationAcknowledgementBannerComponent from '../../common/indication-acknowledgement-banner/IndicationAcknowledgementBanner';
import IndicationSectionPassedReadContent from '../../common/indication-section-passed-read-content/IndicationSectionPassedReadContent';
import IndicationSectionReadContent from '../../common/indication-section-read-content/IndicationSectionReadContent';
import { IndicationSection } from '../../common/indication-section-read-view/IndicationSection';
import ReadButtons from '../../common/indication-section-read-view/ReadButtons';
import UnmarkDuplicateButton from '../../common/indication-section-read-view/UnmarkDuplicateButton';
import IndicationStatus from '../../common/indication-status/IndicationStatus';
import { useIndicationAcknowledgementBanner } from '../indication-acknowledgement-banner/hooks/useIndicationAcknowledgementBanner';
import { useCreateSyndicateDuplicateReadButtonProps } from './hooks/useCreateSyndicateDuplicateReadButtonProps';
import useCreateSyndicateReadButtonProps from './hooks/useCreateSyndicateReadButtonProps';

export type Props = Readonly<{
  onStartEditing: () => void;
  isIndicationEditable: boolean;
  isInvestorCovered: boolean;
  startEditingInvestorContacts: () => void;
}>;

const EMPTY_ARRAY = [];
const IndicationSectionReadView: React.FC<Props> = ({
  onStartEditing,
  isIndicationEditable,
  isInvestorCovered,
  startEditingInvestorContacts,
}) => {
  const { isOrderBookIndicationActivitySubscriptionOn } = useFeatureToggles();
  const { offeringId, basicPublishedOffering, orderBookConfiguration, publishedOffering } =
    useIndicationActivitySyndicateContext();
  const offering = basicPublishedOffering!;
  const indication = offering.orderBook.institutionalIndication!;
  const { canManage: canManagePassiveOrderBook } = useCheckPassiveOrderBookAccess({ offeringId });

  const {
    data: acknowledgementsData,
    loading: isLoadingAcknowledgements,
    error: acknowledgementsError,
    stopPolling,
    startPolling,
    called,
  } = useInstitutionalIndicationAcknowledgementsQuery({
    offeringId,
    indicationId: indication.id,
  });
  usePollingWhenVisible({
    pollInterval: 5_000,
    startPolling,
    stopPolling,
    called,
    isEnabled: !isOrderBookIndicationActivitySubscriptionOn,
  });

  const indicationAcknowledgementsBannerProps = useIndicationAcknowledgementBanner();

  const { readButtonDialogs, ...readButtonProps } = useCreateSyndicateReadButtonProps({
    isIndicationEditable,
    onStartEditing,
  });
  const { duplicateReadButtonDialogs, ...duplicateReadButtonProps } =
    useCreateSyndicateDuplicateReadButtonProps({
      isInvestorCovered,
    });
  const isDuplicate = indication.status === IndicationStatusType.Duplicate;

  if (isLoadingAcknowledgements) {
    return (
      <SectionSkeleton>
        <SectionContentSkeleton />
      </SectionSkeleton>
    );
  }

  return (
    <IndicationSection
      sectionProps={{
        titleItems: <IndicationStatus status={indication.status} />,
        actions: isDuplicate
          ? [
              <UnmarkDuplicateButton key="unmark-duplicate" />,
              <ReadButtons {...duplicateReadButtonProps} key="read-buttons" />,
            ]
          : [<ReadButtons {...readButtonProps} key="read-buttons" />],
      }}
    >
      <Stack gap={1}>
        {acknowledgementsError && <ServerErrorsBanner error={acknowledgementsError} />}
        {!canManagePassiveOrderBook && !acknowledgementsError && isIndicationEditable && (
          <IndicationAcknowledgementBannerComponent
            {...indicationAcknowledgementsBannerProps}
            acknowledgements={acknowledgementsData?.institutionalIndicationAcknowledgements ?? []}
            managers={offering.syndicate.managers}
            startEditingInvestorContacts={startEditingInvestorContacts}
          />
        )}

        {indication.type === InstitutionalIndicationOrderType.Pass && (
          <IndicationSectionPassedReadContent
            submittedOn={indication.auditInfo.createdAt}
            firmName={
              indication.investorInformation.bankInvestorName ??
              indication.investorInformation.cmgEntityName
            }
          />
        )}
        {indication.type !== InstitutionalIndicationOrderType.Pass && (
          <IndicationSectionReadContent
            offeringType={offering.type}
            securityType={defaultTo(publishedOffering?.security.type, null)}
            issuerName={defaultTo(publishedOffering?.issuer.name, null)}
            filings={defaultTo(publishedOffering?.terms.filings, null)}
            submittedOn={indication.auditInfo.createdAt}
            indicationTrancheId={indication.trancheId}
            indicationInstrumentId={indication.instrumentId}
            demandCurrencyCode={indication.currencyCode}
            acknowledgements={acknowledgementsData?.institutionalIndicationAcknowledgements ?? []}
            indicationVersion={indication.version}
            bndAgentCmgEntityKey={indication.billingAndDeliveryAgentCmgEntityKey}
            interestLevels={indication.interestLevels}
            tranches={defaultTo(offering.orderEntryProfile?.tranches, EMPTY_ARRAY)}
            demandInstruments={defaultTo(publishedOffering?.instruments, null)}
            managers={offering.syndicate.managers}
            securityTypeDisplayName={offering.securityTypeDisplayName!}
            isIndicationPriceAsPercentageParEnabled={
              orderBookConfiguration.isIndicationPriceAsPercentageParEnabled
            }
          />
        )}

        {...readButtonDialogs}
        {...duplicateReadButtonDialogs}
      </Stack>
    </IndicationSection>
  );
};

export default IndicationSectionReadView;
